/**
 * GMX Iframe Component (Parent-side)
 *
 * A custom element wrapper for iframes with automatic cross-domain resizing support.
 *
 * Features:
 * - Automatic height syncing with iframe content via postMessage
 * - URL building with query param and hash relay from parent
 * - Secure, origin-aware communication
 * - Optional smooth height transitions (via CSS, using Foundation motion tokens)
 * - Min/max height constraints
 *
 * Supported attributes:
 *   data-set-src                  : Initial iframe URL (auto-built and applied)
 *   data-relay-search="true"      : Pass parent ?query params to iframe URL
 *   data-relay-hash="true"        : Pass parent #hash to iframe URL
 *   data-height-sm                : Fixed pixel height fallback/default up to 899px
 *   data-height-lg                : Fixed pixel height at >= 900px
 *   data-height-max               : Fixed pixel height at >= 1922px
 *   data-responsive="true"        : Enable auto-height behavior
 *   data-allowed-origin           : Enforce strict message origin checks
 *   data-responsive-min-height    : Minimum auto-resized height (px)
 *   data-responsive-max-height    : Maximum auto-resized height (px)
 *   data-animate="true"           : Smooth height transitions
 *   data-iframe-title             : Accessible title applied to the iframe
 *
 * Usage:
 *   <gmx-iframe
 *     data-set-src="https://example.com/page"
 *     data-responsive="true"
 *     data-allowed-origin="https://example.com"
 *     data-animate="true">
 *   </gmx-iframe>
 */

const VERSION = '1.0.0'
const MSG_TYPE_RESIZE = 'responsive-iframe:resize'
const MSG_TYPE_LEGACY_RESIZE = 'childHeightChanged'
const DEFAULT_IFRAME_TITLE = 'Embedded content'
const BREAKPOINT_LG = 900
const BREAKPOINT_MAX = 1922

/**
 * Clamp a value between min and max
 */
function clamp(value: number, min?: number, max?: number): number {
	if (typeof value !== 'number' || isNaN(value)) return value
	if (typeof min === 'number') value = Math.max(min, value)
	if (typeof max === 'number') value = Math.min(max, value)
	return value
}

/**
 * Parse a string to number, returning undefined if invalid
 */
function parseNumber(value: string | null): number | undefined {
	if (value == null || value === '') return undefined
	const n = Number(value)
	return isNaN(n) ? undefined : n
}

interface IframeMetadata {
	lastHeight: number
	initialized: number
}

export class GMXIFrame extends HTMLElement {
	private iframeEl: HTMLIFrameElement | null = null
	private metadata: IframeMetadata | null = null
	private _wasFixedHeight = false
	private _rafPending = false
	private _rafId = 0
	private boundHandleMessage = this._handleMessage.bind(this)
	private boundHandleResize = this._handleResize.bind(this)

	// Attribute getters
	get setSrc(): string | null {
		return this.getAttribute('data-set-src')
	}

	get relaySearch(): boolean {
		return (this.getAttribute('data-relay-search') || '').toLowerCase() === 'true'
	}

	get relayHash(): boolean {
		return (this.getAttribute('data-relay-hash') || '').toLowerCase() === 'true'
	}

	get heightSm(): number | undefined {
		const h = parseNumber(this.getAttribute('data-height-sm'))
		return h !== undefined && h > 0 ? h : undefined
	}

	get heightLg(): number | undefined {
		const h = parseNumber(this.getAttribute('data-height-lg'))
		return h !== undefined && h > 0 ? h : undefined
	}

	get heightMax(): number | undefined {
		const h = parseNumber(this.getAttribute('data-height-max'))
		return h !== undefined && h > 0 ? h : undefined
	}

	get responsive(): boolean {
		return (this.getAttribute('data-responsive') || '').toLowerCase() === 'true'
	}

	get allowedOrigin(): string | null {
		return this.getAttribute('data-allowed-origin')
	}

	get responsiveMinHeight(): number | undefined {
		return parseNumber(this.getAttribute('data-responsive-min-height'))
	}

	get responsiveMaxHeight(): number | undefined {
		return parseNumber(this.getAttribute('data-responsive-max-height'))
	}

	get iframeTitle(): string | null {
		return this.getAttribute('data-iframe-title') || this.getAttribute('data-title') || this.getAttribute('title')
	}

	get legacyResponsiveIframe(): boolean {
		return (this.getAttribute('data-legacy-responsive-iframe') || '').toLowerCase() === 'true'
	}

	connectedCallback(): void {
		this._initIframe()
		window.addEventListener('message', this.boundHandleMessage)
		window.addEventListener('resize', this.boundHandleResize)
	}

	disconnectedCallback(): void {
		window.removeEventListener('message', this.boundHandleMessage)
		window.removeEventListener('resize', this.boundHandleResize)
		if (this._rafPending) {
			cancelAnimationFrame(this._rafId)
			this._rafPending = false
		}
		this.iframeEl = null
		this.metadata = null
	}

	/**
	 * Initialize the iframe element
	 */
	private _initIframe(): void {
		// Check if there's already an iframe child
		this.iframeEl = this.querySelector('iframe')

		if (!this.iframeEl) {
			// Create iframe if not present
			this.iframeEl = document.createElement('iframe')
			this.iframeEl.className = 'gmx-iframe-frame'
			this.appendChild(this.iframeEl)
		}

		// Apply accessibility defaults
		this._applyAccessibilityAttributes()

		// Build and set the src
		const success = this._buildAndSetSrc()
		if (!success) {
			console.warn('[gmx-iframe] Failed to build iframe src')
			return
		}

		// Apply fixed height if set, otherwise apply responsive configuration
		if (this._getResolvedFixedHeight() !== undefined && this.responsive) {
			console.warn(
				'[gmx-iframe] Conflicting configuration: fixed height attributes (data-height-sm/data-height-lg/data-height-max) and data-responsive are both set. Fixed height takes precedence.'
			)
		}

		this._applyHeightBehavior()

		// Store metadata
		this.metadata = {
			lastHeight: 0,
			initialized: Date.now(),
		}
	}

	private _getViewportWidth(): number {
		return window.innerWidth || document.documentElement?.clientWidth || 0
	}

	private _getResolvedFixedHeight(): number | undefined {
		const viewportWidth = this._getViewportWidth()

		if (viewportWidth >= BREAKPOINT_MAX && this.heightMax !== undefined) {
			return this.heightMax
		}

		if (viewportWidth >= BREAKPOINT_LG && this.heightLg !== undefined) {
			return this.heightLg
		}

		return this.heightSm
	}

	private _applyHeightBehavior(): void {
		if (!this.iframeEl) return

		const fixedHeight = this._getResolvedFixedHeight()
		if (fixedHeight !== undefined) {
			this.iframeEl.style.height = `${fixedHeight}px`
			this.iframeEl.removeAttribute('scrolling')
			this.iframeEl.style.removeProperty('overflow')
			this._wasFixedHeight = true
			return
		}

		if (this.responsive) {
			if (this._wasFixedHeight) {
				this.iframeEl.style.height = ''
				this._wasFixedHeight = false
			}
			const allowScroll = typeof this.responsiveMaxHeight === 'number'
			this.iframeEl.setAttribute('scrolling', allowScroll ? 'auto' : 'no')
			if (allowScroll) {
				this.iframeEl.style.overflow = 'auto'
			} else {
				this.iframeEl.style.removeProperty('overflow')
			}
			return
		}

		this._wasFixedHeight = false
		this.iframeEl.style.height = ''
	}

	private _hasBreakpointHeights(): boolean {
		return this.heightSm !== undefined || this.heightLg !== undefined || this.heightMax !== undefined
	}

	private _handleResize(): void {
		if (!this._hasBreakpointHeights()) return
		if (this._rafPending) return
		this._rafPending = true
		this._rafId = requestAnimationFrame(() => {
			this._rafPending = false
			this._applyHeightBehavior()
		})
	}

	/**
	 * Apply accessibility-related attributes to the iframe
	 */
	private _applyAccessibilityAttributes(): void {
		if (!this.iframeEl) return

		const providedTitle = (this.iframeTitle || '').trim()
		const existingTitle = (this.iframeEl.getAttribute('title') || '').trim()
		const resolvedTitle = providedTitle || existingTitle || DEFAULT_IFRAME_TITLE

		this.iframeEl.setAttribute('title', resolvedTitle)

		if (!this.iframeEl.getAttribute('aria-label')) {
			this.iframeEl.setAttribute('aria-label', resolvedTitle)
		}
	}

	/**
	 * Resolve the expected origin for resize messages.
	 * Prefer explicit data-allowed-origin; otherwise infer from iframe src when possible.
	 */
	private _getExpectedOrigin(): string | undefined {
		const allowedOrigin = this.allowedOrigin
		if (allowedOrigin && allowedOrigin !== '*') {
			return allowedOrigin
		}

		const src = this.iframeEl?.src
		if (!src) {
			return undefined
		}

		try {
			const inferredOrigin = new URL(src, window.location.href).origin

			// about:blank and similar "null" origins cannot be matched reliably
			if (inferredOrigin === 'null' || inferredOrigin === 'about:blank') {
				return undefined
			}

			return inferredOrigin
		} catch {
			return undefined
		}
	}

	/**
	 * Build the iframe src URL with optional query/hash relay
	 */
	private _buildAndSetSrc(): boolean {
		if (!this.iframeEl) return false

		const setSrc = this.setSrc
		if (!setSrc) {
			// No data-set-src, check for existing src attribute on iframe
			return this.iframeEl.hasAttribute('src')
		}

		let url: URL
		try {
			url = new URL(setSrc, window.location.href)
		} catch (e) {
			console.error('[gmx-iframe] Invalid data-set-src value:', setSrc, e)
			this.setAttribute('data-responsive-error', 'invalid-src')
			return false
		}

		// Relay parent search params
		if (this.relaySearch && window.location.search) {
			const parentParams = new URLSearchParams(window.location.search)
			parentParams.forEach((value, key) => {
				if (!url.searchParams.has(key)) {
					url.searchParams.append(key, value)
				}
			})
		}

		// Relay parent hash
		if (this.relayHash && window.location.hash) {
			url.hash = window.location.hash
		}

		this.iframeEl.src = url.toString()
		return true
	}

	/**
	 * Handle postMessage events from child iframes
	 */
	private _handleMessage(event: MessageEvent): void {
		const normalizedMessage = this._normalizeMessageData(event.data)

		if (!normalizedMessage) {
			return
		}

		// Verify message is from our iframe
		if (!this.iframeEl || event.source !== this.iframeEl.contentWindow) {
			return
		}

		// Security: Origin validation
		const expectedOrigin = this._getExpectedOrigin()
		if (expectedOrigin && event.origin !== expectedOrigin) {
			console.warn(
				'[gmx-iframe] Rejected message from unauthorized origin:',
				event.origin,
				'expected:',
				expectedOrigin
			)
			return
		}

		// Only process if responsive is enabled and fixed height is NOT set
		if (!this.responsive || this._getResolvedFixedHeight() !== undefined) {
			return
		}

		const height = clamp(Number(normalizedMessage.height), this.responsiveMinHeight, this.responsiveMaxHeight)

		if (!height || height <= 0) {
			return
		}

		// Skip if height hasn't changed
		if (this.metadata && this.metadata.lastHeight === height) {
			return
		}

		// Apply height
		this.iframeEl.style.height = `${height}px`

		// Update metadata
		if (this.metadata) {
			this.metadata.lastHeight = height
		}

		// Dispatch custom event for external listeners
		this.dispatchEvent(
			new CustomEvent('gmx-iframe:resize', {
				detail: { height },
				bubbles: true,
			})
		)
	}

	/**
	 * Normalize the incoming message payload from modern and legacy scripts
	 */
	private _normalizeMessageData(raw: unknown): { height: number; type: 'modern' | 'legacy' } | null {
		let payload: unknown = raw

		if (typeof raw === 'string') {
			try {
				payload = JSON.parse(raw)
			} catch {
				return null
			}
		}

		if (!payload || typeof payload !== 'object') {
			return null
		}

		const normalizedPayload = payload as Record<string, unknown>

		if (normalizedPayload.type === MSG_TYPE_RESIZE) {
			return { height: normalizedPayload.height as number, type: 'modern' }
		}

		if (this.legacyResponsiveIframe && normalizedPayload.msgType === MSG_TYPE_LEGACY_RESIZE) {
			return { height: normalizedPayload.height as number, type: 'legacy' }
		}

		return null
	}

	/**
	 * Get the internal iframe element
	 */
	getIframe(): HTMLIFrameElement | null {
		return this.iframeEl
	}

	/**
	 * Get statistics about this iframe instance
	 */
	getStats(): object {
		const fixedHeight = this._getResolvedFixedHeight()

		return {
			version: VERSION,
			src: this.iframeEl?.src || null,
			lastHeight: this.metadata?.lastHeight || 0,
			initialized: this.metadata?.initialized || null,
			fixedHeight: fixedHeight ?? null,
			responsive: this.responsive,
			legacyResponsiveIframe: this.legacyResponsiveIframe,
			allowedOrigin: this.allowedOrigin,
			title: this.iframeEl?.getAttribute('title') || null,
		}
	}

	/**
	 * Library version
	 */
	static get version(): string {
		return VERSION
	}
}

if (!customElements.get('gmx-iframe')) {
	customElements.define('gmx-iframe', GMXIFrame)
}
